import { useEffect, useRef, useState, type TransitionEvent } from "react";
import { ListPlus, MessageSquareText, Trash2 } from "lucide-react";
import { QuantityStepper } from "@/components/QuantityStepper";
import { MenuItemImage } from "@/components/menu/MenuItemImage";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { calcLineTotal } from "@/lib/cart";
import { formatPrice, loc } from "@/lib/format";
import type { CartLine } from "@/lib/types";
import { cn } from "@/lib/utils";

interface Props {
  line: CartLine;
  onQuantity?: (qty: number) => void | undefined;
  onRemove?: (() => void) | undefined;
  readOnly?: boolean | undefined;
  comfortable?: boolean | undefined;
  card?: boolean | undefined;
  compact?: boolean | undefined;
  totals?: { gross: number; net: number } | undefined;
  currency?: string | undefined;
}

export function CartLineRow({
  line,
  onQuantity,
  onRemove,
  readOnly,
  comfortable = false,
  card = false,
  compact = false,
  totals,
  currency,
}: Props) {
  const { t, lang } = useI18n();
  const [removing, setRemoving] = useState(false);
  const pendingRemoval = useRef<(() => void) | null>(null);
  const removalFallback = useRef<number | null>(null);
  const note = line.note?.trim();
  const hasDetails = line.additions.length > 0 || Boolean(note);
  const unitPrice = line.unitPrice + line.additions.reduce((sum, addon) => sum + addon.price, 0);
  const originalUnitPrice =
    line.originalUnitPrice != null && line.originalUnitPrice > unitPrice
      ? line.originalUnitPrice
      : null;

  useEffect(
    () => () => {
      if (removalFallback.current !== null)
        window.clearTimeout(removalFallback.current);
    },
    [],
  );

  const completeRemoval = () => {
    const callback = pendingRemoval.current;
    if (!callback) return;
    pendingRemoval.current = null;
    if (removalFallback.current !== null) {
      window.clearTimeout(removalFallback.current);
      removalFallback.current = null;
    }
    callback();
  };

  const beginRemoval = (callback: (() => void) | undefined) => {
    if (!callback || removing) return;
    pendingRemoval.current = callback;
    setRemoving(true);
    removalFallback.current = window.setTimeout(completeRemoval, 240);
  };

  const finishOnOpacity = (event: TransitionEvent<HTMLDivElement>) => {
    if (
      removing &&
      event.target === event.currentTarget &&
      event.propertyName === "opacity"
    ) {
      completeRemoval();
    }
  };

  return (
    <div
      className={cn(
        "motion-cart-line flex gap-3",
        card
          ? "rounded-2xl border border-border bg-card p-3 shadow-sm sm:p-4"
          : "border-b border-border last:border-0",
        !card && (comfortable ? "py-4 sm:gap-4 sm:py-5" : "py-3"),
        comfortable && "sm:gap-4",
      )}
      data-removing={removing}
      aria-hidden={removing}
      inert={removing}
      onTransitionEnd={finishOnOpacity}
    >
      <MenuItemImage
        source={line.image}
        name={loc(line.name, lang)}
        sizes={comfortable ? "(min-width: 640px) 80px, 64px" : "56px"}
        loading="lazy"
        className={cn(
          "shrink-0 rounded-2xl object-cover",
          comfortable ? "size-16 sm:size-20" : "size-14",
        )}
      />
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            compact
              ? "grid gap-1"
              : "flex flex-wrap items-start justify-between gap-2",
          )}
        >
          <div className="min-w-0">
            <p
              className={cn(
                "break-words font-bold",
                comfortable ? "text-base" : "text-sm",
              )}
            >
              {loc(line.name, lang)}
            </p>
            {line.variationName ? (
              <p className="truncate text-xs text-muted-foreground">
                {loc(line.variationName, lang)}
              </p>
            ) : null}
            {originalUnitPrice != null && (
              <span className="mt-1 inline-block text-xs font-semibold text-success">
                {t("orders.salePrice")}
              </span>
            )}
          </div>
          <p
            className={cn(
              "shrink-0 font-extrabold text-foreground",
              comfortable ? "text-base" : "text-sm",
              compact && "leading-tight",
            )}
          >
            {totals && totals.net < totals.gross && (
              <del className="me-2 text-xs font-normal text-muted-foreground">
                {formatPrice(totals.gross, lang, currency)}
              </del>
            )}
            {!totals && originalUnitPrice != null && (
              <del className="me-2 text-xs font-normal text-muted-foreground">
                <span className="sr-only">{t("menu.previousPrice")} </span>
                {formatPrice(originalUnitPrice * line.quantity, lang, currency)}
              </del>
            )}
            {formatPrice(totals?.net ?? calcLineTotal(line), lang, currency)}
          </p>
        </div>

        {hasDetails ? (
          <div
            className={cn(
              "mt-2 grid border-s-2 border-primary/20 ps-3",
              compact ? "gap-1.5 text-xs" : "gap-2 text-sm",
            )}
          >
            {line.additions.length > 0 ? (
              <div>
                <p className="flex items-center gap-1.5 text-xs font-bold text-muted-foreground">
                  <ListPlus
                    className="size-3.5 text-primary"
                    aria-hidden="true"
                  />
                  {t("menu.additions")}
                </p>
                <ul className="mt-1 grid gap-1 text-foreground/80">
                  {line.additions.map((addition) => (
                    <li
                      key={addition.id}
                      className="flex items-baseline justify-between gap-3"
                    >
                      <span className="min-w-0 break-words">
                        {loc(addition.name, lang)}
                      </span>
                      {addition.price > 0 ? (
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                          +{formatPrice(addition.price, lang, currency)}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {note ? (
              <div>
                <p className="flex items-center gap-1.5 text-xs font-bold text-muted-foreground">
                  <MessageSquareText
                    className="size-3.5 text-primary"
                    aria-hidden="true"
                  />
                  {t("common.note")}
                </p>
                <p className="mt-1 whitespace-pre-wrap break-words leading-5 text-foreground/80">
                  {note}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        {readOnly ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {line.quantity} ×{" "}
            {originalUnitPrice != null && (
              <del className="me-1">
                <span className="sr-only">{t("menu.previousPrice")} </span>
                {formatPrice(originalUnitPrice, lang, currency)}
              </del>
            )}
            <span className={originalUnitPrice != null ? "font-semibold text-foreground" : ""}>
              {formatPrice(unitPrice, lang, currency)}
            </span>
          </p>
        ) : (
          <div
            className={cn(
              "mt-2 flex flex-wrap items-center gap-2",
              compact &&
                "mt-3 justify-between border-t border-border/60 pt-2.5",
            )}
          >
            <QuantityStepper
              value={line.quantity}
              size={compact ? "compact" : "default"}
              label={`${t("common.quantity")} · ${loc(line.name, lang)}`}
              min={1}
              onChange={(quantity) => {
                if (quantity <= 0)
                  beginRemoval(
                    onQuantity ? () => onQuantity(quantity) : undefined,
                  );
                else onQuantity?.(quantity);
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              className={cn(compact && "size-9 shrink-0")}
              aria-label={t("cart.removeItem", { name: loc(line.name, lang) })}
              onClick={(event) =>
                event.detail === 0 ? onRemove?.() : beginRemoval(onRemove)
              }
            >
              <Trash2 className="size-4 text-destructive" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
